import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { IndexType } from "./IndexType";
import { BaseTableDefinitionFactory } from "./BaseTableDefinitionFactory";
import { TableDefinition, BfxColDef } from "./TableDefinition";
import { BigNumber } from "shared/cubiq-model-multi-asset";
import { BenchmarkBlotterFilterOrderStates, FilterOrderTypes } from "./FilterTypeEnums";
import { BlotterType, AssetClass } from "shared/cubiq-model-multi-asset";
import { Injectable, Optional } from "@angular/core";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
import { CcyLookupService } from "../lookup/model/impl/CcyLookupService";
import { CcyPairLookupService } from "../lookup/model/impl/CcyPairLookupService";
import { ClientLookupService } from "../lookup/model/impl/ClientLookupService";
import { AccountLookupService } from "../lookup/model/impl/AccountLookupService";
import { TraderGroupLookupService } from "../lookup/model/impl/TraderGroupLookupService";
import { SalesGroupLookupService } from "../lookup/model/impl/SalesGroupLookupService";
import { UserLookupService } from "../lookup/model/impl/UserLookupService";
import { SystemConfigPromiseService } from "shared/auth-services";
import { ProfilePromiseActionService } from "../profile/impl/ProfilePromiseActionService";
import { AlgoStrategyLookupService } from "../lookup/model/impl/AlgoStrategyLookupService";
import { BasePricingService } from "../lookup/pricing/impl/BasePricingService";
import { DealtPricingService } from "../lookup/pricing/impl/DealtPricingService";
import { RateSourceLookupService } from "../lookup/model/impl/RateSourceLookupService";
import { FixingSourceLookupService } from "../lookup/model/impl/FixingSourceLookupService";
import { CurrencyPairCalendarLookupService } from "../lookup/model/impl/CurrencyPairCalendarLookupService";

// Helpers & Adapters
import { OrdersTableMode } from "./OrdersTableMode";
import { ColumnFilterType } from "./ColumnFilterType";
import { BmarkTraderGroupNameHelper } from "./benchmarks/helpers/BmarkTradeGroupNameHelper";
import { ProfileSettingKeys } from "./core/ProfileSettingKeys";
import { SysFeatures } from "./core/SysFeatures";
import { StatusIconsHelper } from "./orders/StatusIconsHelper";
import { PercentAwayHelper } from "./orders/PercentAwayHelper";
import { OrderTypeHelper } from "./orders/OrderTypeHelper";
import { OrderStateHelper } from "./orders/OrderStateHelper";
import { ValueDateHelper } from "./orders/ValueDateHelper";
import { NdfTrackingFwdPtsHelper } from "./orders/NdfTrackingFwdPtsHelper";
import {
  ValueGetterParams,
  ValueFormatterParams,
  GetQuickFilterTextParams,
  ITooltipParams,
} from "ag-grid-community";
import { BmarkClientOrdersBlotterMode } from "./benchmarks/enums/BmarkClientOrdersBlotterMode.enum";

// COLUMN DATA NAMES
import {
  COLUMN_ORDER_STATE,
  COLUMN_DESK_ID,
  COLUMN_CLIENT_BRANCH,
  COLUMN_ENTRY_USER_ID,
  COLUMN_ORDER_ENTRY_DATE,
  COLUMN_TRADER_GROUP_NAMES,
  COLUMN_ORDER_EXECUTED_AMOUNT,
  COLUMN_ORDER_EXECUTED_AMOUNT_USD,
  COLUMN_REMAINING_AMOUNT,
  COLUMN_REMAINING_AMOUNT_USD,
  COLUMN_ORDER_STRATEGY_PARAMETERS,
  COLUMN_ORDER_EXPIRY_DATE,
  COLUMN_ORDER_ACTIVATION_DATE,
  COLUMN_ALLOCATION_ID,
  COLUMN_ORDER_TYPE,
  COLUMN_VALUE_DATE,
  FIXING_DATE_COLUMN_PROPERTY,
  FIXING_DATE_UTC_FORMAT,
  REQUESTED_FIXING_DATE_COLUMN_PROPERTY,
  REQUESTED_FIXING_TIMEZONE_COLUMN_PROPERTY,
  REQUESTED_FIXING_TIME_COLUMN_PROPERTY,
  TIME_ZONE_UTC,
  COLUMN_ORDER_EXECUTED_PERC,
} from "./OrdersTableDefinitionFactory.constants";

import {
  COLUMN_TRADE_DESK_ID,
  COLUMN_TRADE_INSTRUMENT,
} from "./TradeTableDefinitionFactory.constants";
import {
  COLUMN_INTER_DESK_ID,
  COLUMN_INTER_INSTRUMENT,
  COLUMN_INTER_ORDER_PRODUCT,
} from "./InterventionTableDefinitionFactory.constants";
import { MultiLineTooltipValueGetter } from "./tooltip/MultiLineTooltipValueGetter";
import { HEDGE_ORDERS_TABLE_DEFINITION_VER } from "./TableDefinitionFactory.constants";
import { ClientModelPrivatization } from "./privatization/ClientModelPrivatization";
import { AccountModelPrivatization } from "./privatization/AccountModelPrivatization";
import { ClientTraderModelPrivatization } from "./privatization/ClientTraderModelPrivatization";
import { PrivatizationHelper } from "./orders/PrivatizationHelper";
import { AtdlTooltipUiRep } from "./adtl/atdl-tooltip-ui-rep";

const DEFAULT_CELL_CLASS: string = "ag-cell-default";
// OMS-30890 Per Keith D, "-" is reserved for empty numerical fields.
import {
  EMPTY_AMT_RATE_VALUE,
  EMPTY_TEXT_VALUE,
} from "shared/cubiq-services/model/BaseTableDefinitionFactory";
import { VolumePerMinuteHelper } from "../mediator/impl/VolumePerMinuteHelper";
import { BfxConfigIDRefHelper } from "../../helper-services/bfx-config-id-ref-helper";
import {
  BM_ALGO_START_TIME_VALUE_GETTER,
  BM_ALGO_END_TIME_VALUE_GETTER,
  BfxHedgeOrderMonitorBlotterColumns,
  COLUMN_CLIENT_ID_DATA,
  COLUMN_CLIENT_NAME_DATA,
  COLUMN_DEALT_CURRENCY_DATA,
  COLUMN_DEALT_DIR_DATA,
  COLUMN_INSTRUMENT_DATA,
  COLUMN_ORDER_AMOUNT_DATA,
  COLUMN_ORDER_FIXING_SOURCE_CODE_DATA,
  COLUMN_ORDER_PRODUCT_PRODUCT,
  COLUMN_ORDER_USD_AMOUNT_DATA,
  COLUMN_TRADE_ORDER_TYPE_DATA,
  COLUMN_TRADE_SALES_GROUP_ID_DATA,
  COLUMN_TRADE_VALUE_DATE_DATA,
  FIXING_DATE_VALUE_GETTER,
  FIXING_TIME_UTC_VALUE_FORMATTER,
  FIXING_TIME_UTC_VALUE_GETTER,
  FIXING_TIME_VALUE_GETTER,
} from "./BmarkHedgeOrdersTableDefinitionFactory.constants";

// CLASS NAMES
const STRONG_CELL_CLASS: string = "blotter-bold-column";

@Injectable()
export class BmarkHedgeOrdersTableDefinitionFactory extends BaseTableDefinitionFactory {
  protected defaultSortModel: any = [{ colId: "creationDate", sort: "desc" }];
  protected mode: OrdersTableMode = OrdersTableMode.DEALT;
  private myClientTraderEnabled: boolean;
  private myPrivateCommentsEnabled: boolean;
  private myMarkupEnabled: boolean;
  protected myNdfEnabled: boolean;
  protected myFwdEnabled: boolean;
  protected myTrailingStopEnabled: boolean;
  protected myAutFillEnabled: boolean;
  protected myBmarkEnabled: boolean;
  protected myPartFillDisabledTypeText: string = "";
  private marketBarEnabled: boolean = true;
  private myStatusIconHelper: StatusIconsHelper;
  private dealtPercentAwayHelper: PercentAwayHelper;
  private basePercentAwayHelper: PercentAwayHelper;
  private myOrderTypeHelper: OrderTypeHelper;
  private myOrderStateHelper: OrderStateHelper;
  private myValueDateHelper: ValueDateHelper;
  private myNdfTrackingFwdPointsHelper: NdfTrackingFwdPtsHelper;
  protected volumePerMinuteHelper: VolumePerMinuteHelper;
  protected privatizationHelper: PrivatizationHelper;
  myBmarkTradeGroupNameHelper: any;

  constructor(
    profileService: ProfilePromiseActionService,
    // It is not possible to inject against an interface
    @Optional() private _ccyLookupService: CcyLookupService,
    @Optional() private _ccyPairLookupService: CcyPairLookupService,
    @Optional() private _clientLookup: ClientLookupService,
    @Optional() private _accountLookup: AccountLookupService,
    @Optional() private _salesGroupLookup: SalesGroupLookupService,
    @Optional() private _traderGroupLookup: TraderGroupLookupService,
    @Optional() private _userLookup: UserLookupService,
    @Optional() private _sysConfigService: SystemConfigPromiseService,
    @Optional() protected _translateService: TranslateService,
    @Optional() protected algoStrategyLookupService: AlgoStrategyLookupService,
    @Optional() protected _rateSourceLookup: RateSourceLookupService,
    @Optional() protected _myAccountLookup: AccountLookupService,
    @Optional() protected _mySalesGroupLookup: SalesGroupLookupService,
    @Optional() protected _myTraderGroupLookup: TraderGroupLookupService,
    @Optional() protected _userLookupService: UserLookupService,
    @Optional() protected _myBasePricingService: BasePricingService,
    @Optional() protected _myDealtPricingService: DealtPricingService,
    @Optional() protected _fixingSourceLookupService: FixingSourceLookupService,
    @Optional()
    protected _currencyPairCalendarLookupService: CurrencyPairCalendarLookupService,
    @Optional() protected cmp: ClientModelPrivatization,
    @Optional() protected amp: AccountModelPrivatization,
    @Optional() protected ctmp: ClientTraderModelPrivatization,
    @Optional() protected atdlTooltipUiRep: AtdlTooltipUiRep,
  ) {
    super(profileService);

    this.dealtPercentAwayHelper = new PercentAwayHelper(
      this._myDealtPricingService,
      profileService,
      this._ccyPairLookupService,
    );
    this.basePercentAwayHelper = new PercentAwayHelper(
      this._myBasePricingService,
      profileService,
      this._ccyPairLookupService,
    );
    this.myOrderTypeHelper = new OrderTypeHelper(
      this.myPartFillDisabledTypeText,
      this.algoStrategyLookupService,
    );
    this.myOrderStateHelper = new OrderStateHelper(COLUMN_ORDER_STATE, this._translateService);
    this.myStatusIconHelper = new StatusIconsHelper(
      this._userLookupService,
      this._sysConfigService,
      this._translateService,
      this._myDealtPricingService,
      null, // No TSL support
      this._myTraderGroupLookup,
    );
    this.myValueDateHelper = new ValueDateHelper(this._currencyPairCalendarLookupService);
    this.myNdfTrackingFwdPointsHelper = new NdfTrackingFwdPtsHelper(
      this._ccyPairLookupService,
      this._myBasePricingService,
      this._myDealtPricingService,
    );
    this.myBmarkTradeGroupNameHelper = new BmarkTraderGroupNameHelper(
      this._traderGroupLookup,
      COLUMN_DESK_ID,
    );
    this.privatizationHelper = new PrivatizationHelper(_clientLookup, _accountLookup);
    this.volumePerMinuteHelper = new VolumePerMinuteHelper(
      this._traderGroupLookup,
      this._currencyPairCalendarLookupService,
    );

    this.profileService.getProfileConfig(this.getProfileSettingKey()).then((profileValue) => {
      this.mapUserSettings(profileValue);
    });

    this.profileService
      .getProfileConfigUpdates(this.getProfileSettingKey())
      .subscribe((profileValue) => {
        this.mapUserSettings(profileValue);
      });
  }

  // this override for system configs applicable for blotter definition
  protected getTableCongiPromises(): Array<Promise<any>> {
    return [
      this._sysConfigService.getSystemConfig(SysFeatures.clientTraderUsersEnabled),
      this._sysConfigService.getSystemConfig(SysFeatures.featuresEnablePrivateComments),
      this._sysConfigService.getSystemConfig(SysFeatures.markupEnabled),
      this._sysConfigService.getSystemConfig(SysFeatures.ndfEnabled),
      this._sysConfigService.getSystemConfig(SysFeatures.fwdEnabled),
      // promises related to services
      this.atdlTooltipUiRep.isReady(),
    ];
  }

  protected determineDefaultConfig(defaultConfig: any) {
    super.determineDefaultConfig(defaultConfig);
    if (defaultConfig) {
      if (defaultConfig.dealtMode != null) {
        this.mode = defaultConfig.dealtMode ? OrdersTableMode.DEALT : OrdersTableMode.BASE;
      }
      if (defaultConfig.ndfMode != null && defaultConfig.ndfMode) {
        this.mode =
          this.mode === OrdersTableMode.BASE ? OrdersTableMode.NDF_BASE : OrdersTableMode.NDF_DEALT;
      }
    }
  }

  // this override to set flags returned by promises
  protected handleTableCongiPromises(values: Array<any>) {
    this.myClientTraderEnabled = this.valueOn(values[0]);
    this.myPrivateCommentsEnabled = this.valueOn(values[1]);
    this.myMarkupEnabled = this.valueOn(values[2]);
    this.myNdfEnabled = this.valueOn(values[3]);
    this.myFwdEnabled = this.valueOn(values[4]);
  }

  private initColdDef(): any {
    return {
      suppressMenu: true,
      cellClass: [DEFAULT_CELL_CLASS],
      isPriority: false,
      hide: false,
    };
  }

  public createDef(context: any = null): TableDefinition {
    super.createDef(context);

    let embeddedMode: boolean = context != null && context.embeddedMode === true;

    logger.out.info(`${this.constructor.name}.createDef() ${OrdersTableMode[this.mode]}`);
    let fwdColsVisibleByDefault: boolean = this.myNdfEnabled || this.myFwdEnabled;

    let position = this.position;
    let td = new TableDefinition("primaryKey", "as_sort_id");
    td.setOverwriteKey("orderId");

    // requestSubmissionTime's column
    td.setDefaultSort(1, "desc");

    td.benchmarkTableDefinition = true;

    /**
     * Selection column
     */
    let selectionColumn: BfxColDef = this.initColdDef();
    selectionColumn.field = "selection";
    selectionColumn.sortingOrder = [];
    selectionColumn.pinned = "left";
    selectionColumn.lockPosition = true;
    selectionColumn.checkboxSelection = true;
    selectionColumn.headerName = "(0)";
    selectionColumn.headerCheckboxSelection = true;
    selectionColumn.headerCheckboxSelectionFilteredOnly = true;
    selectionColumn.suppressSorting = true;
    selectionColumn.suppressMovable = true;
    selectionColumn.suppressMenu = true;
    selectionColumn.columnFilterType = ColumnFilterType.NONE;
    selectionColumn.visibleInEmbeddedMode = true;
    selectionColumn.valueGetter = function (params) {
      return this.myStatusIconHelper.getIcons(params.data);
    }.bind(this);
    selectionColumn.cellRenderer = function (params) {
      return params.value != null ? params.value : "";
    };
    selectionColumn.tooltipValueGetter = () => null;
    // enforced width on fixed column
    selectionColumn.cellClass = ["status-icon-cell"];
    selectionColumn.width = 96;
    selectionColumn.minWidth = this.threeWs;
    selectionColumn.maxWidth = 96;
    selectionColumn.resizable = true;
    selectionColumn.position = this.position.next();
    selectionColumn.omitFromPicker = true;
    selectionColumn.disableInPicker = true;
    selectionColumn.indexType = IndexType.None;

    td.addColumn(selectionColumn);

    /** Auto-Hedge column */
    const autoHedge: BfxColDef = this.initColdDef();
    autoHedge.field = "hedgingMode";
    //  autoHedge.pinned = "left";
    autoHedge.suppressMovable = true;
    autoHedge.lockPosition = true;

    autoHedge.valueGetter = (params) => {
      return {
        isAutoHedge:
          (params.data as CubiqTS.OrderData)?.hedgingMode === CubiqTS.HedgingModeType.AUTO,
        isAcceptance: params.data?.orderState === CubiqTS.OrderState.ACCEPTANCE_NEEDED,
      };
    };
    autoHedge.comparator = (valueA, valueB) => {
      const hedgeValueA = valueA.isAutoHedge ? (valueA.isAcceptance ? 2 : 1) : 0;
      const hedgeValueB = valueB.isAutoHedge ? (valueB.isAcceptance ? 2 : 1) : 0;
      return hedgeValueA - hedgeValueB;
    };
    autoHedge.headerName = "AH";
    autoHedge.sortable = true;
    autoHedge.resizable = true;
    autoHedge.suppressMenu = true;
    autoHedge.columnFilterType = ColumnFilterType.NONE;
    autoHedge.visibleInEmbeddedMode = true;
    autoHedge.cellRenderer = "autoHedgeRenderer";
    autoHedge.cellClass = ["blotter_center_align_col"];
    autoHedge.tooltipValueGetter = () => null;
    // enforced width on fixed column
    //  autoHedge.cellClass = ["status-icon-cell"];
    autoHedge.width = 34;
    autoHedge.minWidth = this.oneWs;
    autoHedge.maxWidth = 34;
    autoHedge.position = this.position.next();
    autoHedge.omitFromPicker = true;
    autoHedge.disableInPicker = true;
    //  autoHedge.colSelectorName = "Auto-Hedge (AH)";
    autoHedge.headerTooltip = "Auto-Hedge Order";

    td.addColumn(autoHedge);

    // order state
    let orderStateColumn: BfxColDef = this.initColdDef();
    orderStateColumn.headerName = "Order State";
    orderStateColumn.indexType = IndexType.String;
    orderStateColumn.field = COLUMN_ORDER_STATE;
    orderStateColumn.minWidth = this.oneWs;
    orderStateColumn.width = this.sixWs;
    orderStateColumn.columnFilterType = ColumnFilterType.ORDERSTATE;
    orderStateColumn.columnFilterData = BenchmarkBlotterFilterOrderStates;
    orderStateColumn.visibleInEmbeddedMode = true;
    orderStateColumn.isEmbeddedColumnPriority = false;
    orderStateColumn.embeddedModePosition =
      BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_ORDER_STATE;
    orderStateColumn.valueGetter = function (params) {
      return this.myOrderStateHelper.getState(params);
    }.bind(this);
    orderStateColumn.cellClass = ["blotter_left_align_col"];
    if (!this.restoreFromConfig(orderStateColumn)) {
      orderStateColumn.position = this.position.next();
    }

    td.addColumn(orderStateColumn);

    // orderId
    let orderIdColumn: BfxColDef = this.initColdDef();
    orderIdColumn.headerName = "Order ID";
    orderIdColumn.field = "orderId";
    orderIdColumn.minWidth = this.oneWs;
    orderIdColumn.width = BfxConfigIDRefHelper.getOrderRefWs();
    orderIdColumn.columnFilterType = ColumnFilterType.NONE;
    orderIdColumn.visibleInEmbeddedMode = true;
    orderIdColumn.isEmbeddedColumnPriority = false;
    orderIdColumn.embeddedModePosition =
      BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_ORDER_ID;
    orderIdColumn.valueFormatter = BfxConfigIDRefHelper.orderRefValueFormatter;
    orderIdColumn.getQuickFilterText = BfxConfigIDRefHelper.orderRefValueFormatter;
    if (!this.restoreFromConfig(orderIdColumn)) {
      orderIdColumn.hide = true;
      orderIdColumn.position = this.position.next();
    }

    td.addColumn(orderIdColumn);

    // client name
    let clNm: BfxColDef = this.initColdDef();
    clNm.headerName = "Client Name";
    clNm.field = COLUMN_CLIENT_NAME_DATA;
    clNm.indexType = IndexType.String;
    clNm.minWidth = this.oneWs;
    clNm.width = this.tenWs;
    clNm.columnFilterType = ColumnFilterType.CLIENT;
    clNm.visibleInEmbeddedMode = true;
    clNm.isEmbeddedColumnPriority = false;
    clNm.embeddedModePosition = BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_CLIENT_NAME;
    clNm.valueGetter = function (params) {
      return this.privatizationHelper.getPrivatizedValue(params, COLUMN_CLIENT_NAME_DATA);
    }.bind(this);
    clNm.getFilterValue = (params) => {
      return this.privatizationHelper.getPrivatizedValue({ data: params }, COLUMN_CLIENT_ID_DATA);
    };
    clNm.cellClass = ["blotter_left_align_col"];
    if (!this.restoreFromConfig(clNm)) {
      clNm.hide = true;
      clNm.position = this.position.next();
    }

    td.addColumn(clNm);

    // client id
    let clientId: BfxColDef = this.initColdDef();
    clientId.headerName = "Client ID";
    clientId.field = COLUMN_CLIENT_ID_DATA;
    clientId.indexType = IndexType.String;
    clientId.minWidth = this.oneWs;
    clientId.width = this.tenWs;
    clientId.columnFilterType = ColumnFilterType.CLIENTID;
    clientId.visibleInEmbeddedMode = true;
    clientId.isEmbeddedColumnPriority = false;
    clientId.embeddedModePosition = BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_CLIENT_ID;
    clientId.valueGetter = function (params) {
      return this.privatizationHelper.getPrivatizedValue(params, COLUMN_CLIENT_ID_DATA);
    }.bind(this);
    if (!this.restoreFromConfig(clientId)) {
      clientId.hide = true;
      clientId.position = this.position.next();
    }

    td.addColumn(clientId);

    // account name
    let accName: BfxColDef = this.initColdDef();
    accName.headerName = "Account Name";
    accName.field = "accountName";
    accName.indexType = IndexType.String;
    accName.minWidth = this.oneWs;
    accName.width = this.tenWs;
    accName.columnFilterType = ColumnFilterType.ACCOUNT;
    accName.visibleInEmbeddedMode = true;
    accName.isEmbeddedColumnPriority = false;
    accName.embeddedModePosition = BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_ACCOUNT_NAME;
    accName.valueGetter = function (params: ValueGetterParams) {
      let accountName: string = params.data.accountName;

      if (accountName == null && params.data.accountCode != null) {
        let account: CubiqTS.AccountImpl = this._accountLookup.getAccount(params.data.accountCode);
        if (account != null) {
          accountName = account.name;
        }
      } else {
        accountName = this.privatizationHelper.getPrivatizedValue(params, "accountName");
      }

      return accountName;
    }.bind(this);
    accName.getFilterValue = (params: any) => {
      return this.privatizationHelper.getPrivatizedValue({ data: params }, "accountCode");
    };
    if (!this.restoreFromConfig(accName)) {
      accName.hide = embeddedMode;
      accName.position = this.position.next();
    }

    td.addColumn(accName);

    // account id
    let accId: BfxColDef = this.initColdDef();
    accId.headerName = "Account ID";
    accId.field = "accountCode";
    accId.indexType = IndexType.String;
    accId.minWidth = this.oneWs;
    accId.width = this.tenWs;
    accId.columnFilterType = ColumnFilterType.ACCOUNTID;
    accId.visibleInEmbeddedMode = true;
    accId.isEmbeddedColumnPriority = false;
    accId.embeddedModePosition = BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_ACCOUNT_ID;
    accId.valueGetter = function (params) {
      return this.privatizationHelper.getPrivatizedValue(params, "accountCode");
    }.bind(this);
    if (!this.restoreFromConfig(accId)) {
      accId.hide = true;
      accId.position = this.position.next();
    }

    td.addColumn(accId);

    // dealt pair
    let instrumentCol: BfxColDef = this.initColdDef();
    instrumentCol.headerName = "Instrument";
    instrumentCol.field = COLUMN_INSTRUMENT_DATA;
    instrumentCol.headerTooltip = "Instrument (Dealt)";
    instrumentCol.indexType = IndexType.String;
    instrumentCol.alias = [COLUMN_TRADE_INSTRUMENT, COLUMN_INTER_INSTRUMENT];
    instrumentCol.minWidth = this.oneWs;
    instrumentCol.width = this.sixWs;
    instrumentCol.omitFromPicker = false;
    instrumentCol.isPriority = true;
    instrumentCol.cellClass = ["blotter_left_align_col" + " " + STRONG_CELL_CLASS];
    instrumentCol.columnFilterType = ColumnFilterType.CURRENCY_PAIR;
    instrumentCol.visibleInEmbeddedMode = true;
    instrumentCol.disableInPicker = embeddedMode;
    instrumentCol.isEmbeddedColumnPriority = false;
    instrumentCol.embeddedModePosition =
      BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_INSTRUMENT;
    instrumentCol.valueGetter = function (params) {
      if (
        params.data.blotterMode === BmarkClientOrdersBlotterMode.RISK &&
        Array.isArray(params.data.allocationData) &&
        params.data.allocationData[0] instanceof CubiqTS.AllocationDataImpl
      ) {
        return (
          params.data.allocationData[0].baseCcy + "/" + params.data.allocationData[0].nonbaseCcy
        );
      } else {
        return params.data["isInvertedOrderFg"]
          ? params.data["nonBaseCode"] + "/" + params.data["baseCode"]
          : params.data["baseCode"] + "/" + params.data["nonBaseCode"];
      }
    };
    if (!this.restoreFromConfig(instrumentCol)) {
      instrumentCol.hide = false;
      instrumentCol.position = this.position.next();
    }

    td.addColumn(instrumentCol);

    // dealt direction
    let intentCol: BfxColDef = this.initColdDef();

    intentCol.headerName = "Dir";
    intentCol.headerTooltip = "Direction (Dealt)";
    intentCol.field = COLUMN_DEALT_DIR_DATA;
    intentCol.indexType = IndexType.String;
    intentCol.minWidth = this.twoWs;
    intentCol.width = this.threeWs;
    intentCol.cellRenderer = "intentRenderer";
    intentCol.cellClass = "blotter_center_align_col intent_col";
    intentCol.isPriority = true;
    intentCol.columnFilterType = ColumnFilterType.NONE;
    intentCol.columnFilterData = CubiqTS.OrderIntent;
    intentCol.disableInPicker = embeddedMode;
    intentCol.visibleInEmbeddedMode = true;
    intentCol.isEmbeddedColumnPriority = true;
    intentCol.embeddedModePosition = BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_DIR;
    intentCol.valueGetter = function (params) {
      let order: CubiqTS.OrderDataImpl = params.data;
      let clientIntent: CubiqTS.OrderIntent = params.data.clientIntent;
      if (order.allocationData != null && order.allocationData.length > 0) {
        clientIntent = order.allocationData[0].allocationIntent;
      }
      switch (clientIntent) {
        case CubiqTS.OrderIntent.BUY:
          return "Buy";
        case CubiqTS.OrderIntent.SELL:
          return "Sell";
      }
    };
    if (!this.restoreFromConfig(intentCol)) {
      intentCol.hide = false;
      intentCol.position = this.position.next();
    }

    td.addColumn(intentCol);

    // Dealt ccy
    let dealtCcy: BfxColDef = this.initColdDef();
    dealtCcy.headerName = "Ccy";
    dealtCcy.headerTooltip = "Dealt Currency";
    dealtCcy.field = COLUMN_DEALT_CURRENCY_DATA;
    dealtCcy.indexType = IndexType.String;
    dealtCcy.minWidth = this.oneWs;
    dealtCcy.width = this.threeWs;
    dealtCcy.isPriority = true;
    dealtCcy.valueGetter = function (params) {
      if (
        params.data.blotterMode === BmarkClientOrdersBlotterMode.RISK &&
        Array.isArray(params.data.allocationData) &&
        params.data.allocationData[0] instanceof CubiqTS.AllocationDataImpl
      ) {
        return params.data.allocationData[0].fixedCcy;
      } else {
        return params.data.fixedCode;
      }
    };
    dealtCcy.cellClass = ["blotter_center_align_col" + " " + STRONG_CELL_CLASS];
    dealtCcy.isPriority = true;
    dealtCcy.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(dealtCcy)) {
      dealtCcy.hide = false;
      dealtCcy.position = this.position.next();
    }

    td.addColumn(dealtCcy);

    // dealt entered amount
    let orderAmtCd: BfxColDef = this.initColdDef();
    orderAmtCd.headerName = "Order Amt";
    orderAmtCd.headerTooltip = "Order Amount (Dealt)";
    orderAmtCd.field = COLUMN_ORDER_AMOUNT_DATA;
    orderAmtCd.indexType = IndexType.NaturalString;
    orderAmtCd.minWidth = 90;
    orderAmtCd.width = this.eightWs;
    orderAmtCd.isPriority = true;
    orderAmtCd.cellClass = ["blotter_amt_align" + " " + STRONG_CELL_CLASS];
    orderAmtCd.valueFormatter = this.formatAmts;
    orderAmtCd.columnFilterType = ColumnFilterType.INT;
    orderAmtCd.columnFilterData = null;
    orderAmtCd.visibleInEmbeddedMode = true;
    orderAmtCd.isEmbeddedColumnPriority = true;
    orderAmtCd.embeddedModePosition = BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_ORDER_AMT;
    orderAmtCd.valueGetter = function (params) {
      if (
        params.data != null &&
        Array.isArray(params.data.allocationData) &&
        params.data.allocationData[0] instanceof CubiqTS.AllocationDataImpl
      ) {
        return params.data.allocationData[0].allocationQty;
      } else if (params.data != null) {
        return params.data.orderQty;
      }
    };
    // if (orderAmtCd.visibleInEmbeddedMode && this.restoreFromConfig(orderAmtCd)) {
    //     orderAmtCd.position = BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_ORDER_AMT;
    // }
    if (!this.restoreFromConfig(orderAmtCd)) {
      orderAmtCd.position = this.position.next();
    }

    td.addColumn(orderAmtCd);

    // usd entered amount
    const usdAmtCd: BfxColDef = this.initColdDef();
    usdAmtCd.headerName = "Order Amt USD";
    usdAmtCd.headerTooltip = "Order Amount in USD Terms";
    usdAmtCd.field = COLUMN_ORDER_USD_AMOUNT_DATA;
    usdAmtCd.indexType = IndexType.NaturalString;
    usdAmtCd.minWidth = this.oneWs;
    usdAmtCd.width = this.eightWs;
    usdAmtCd.cellClass = ["blotter_amt_align"];
    usdAmtCd.valueFormatter = this.formatAmts;
    usdAmtCd.columnFilterType = ColumnFilterType.NUMBER;
    usdAmtCd.columnFilterData = null;
    usdAmtCd.valueGetter = function (params) {
      if (
        Array.isArray(params.data.allocationData) &&
        params.data.allocationData.length > 1 &&
        params.data.allocationData[0] instanceof CubiqTS.AllocationDataImpl
      ) {
        return params.data.allocationData[0].usdEquivQty; //.toString();
      } else {
        return params.data.usdEquivQty; //.toString();
      }
    };
    if (!this.restoreFromConfig(usdAmtCd)) {
      usdAmtCd.hide = true;
      usdAmtCd.position = this.position.next();
    }

    td.addColumn(usdAmtCd);

    // dealt execution percent
    const fillPerc: BfxColDef = this.initColdDef();
    fillPerc.field = COLUMN_ORDER_EXECUTED_PERC;
    fillPerc.headerName = "Fill %";
    fillPerc.indexType = IndexType.NaturalString;
    fillPerc.columnFilterType = ColumnFilterType.PERCENTAGE;
    fillPerc.minWidth = this.oneWs;
    fillPerc.width = this.fiveWs;
    fillPerc.isPriority = true;
    fillPerc.cellClass = ["blotter_amt_align"];
    fillPerc.visibleInEmbeddedMode = true;
    fillPerc.columnFilterData = null;
    fillPerc.valueFormatter = this.formatPercent;
    fillPerc.cellRenderer = "fillPercRenderer";

    if (!this.restoreFromConfig(fillPerc)) {
      fillPerc.hide = true;
      fillPerc.position = this.position.next();
    }

    td.addColumn(fillPerc);

    const executionAmtCd: BfxColDef = this.initColdDef();
    executionAmtCd.headerName = "Fill Amt";
    executionAmtCd.field = COLUMN_ORDER_EXECUTED_AMOUNT;
    executionAmtCd.indexType = IndexType.NaturalString;
    executionAmtCd.minWidth = 90;
    executionAmtCd.width = this.eightWs;
    executionAmtCd.isPriority = true;
    executionAmtCd.visibleInEmbeddedMode = true;
    executionAmtCd.isEmbeddedColumnPriority = true;
    executionAmtCd.getQuickFilterText = () => null;
    executionAmtCd.embeddedModePosition =
      BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_FILL_AMT;
    executionAmtCd.cellClass = ["blotter_amt_align"];
    executionAmtCd.columnFilterType = ColumnFilterType.NUMBER;
    executionAmtCd.columnFilterData = null;
    executionAmtCd.valueFormatter = this.formatAmts;
    executionAmtCd.tooltipValueGetter = (params: ITooltipParams) => {
      let orderAmt = null;
      let fillAmt = null;
      let colDef = params.colDef;

      if (typeof colDef.valueGetter == "string") {
        fillAmt = colDef.valueGetter;
      } else if (typeof colDef.valueGetter == "function") {
        fillAmt = colDef.valueGetter(params as any);
      }

      orderAmt = getOrderAmount(params as any);

      if (fillAmt != null && orderAmt != null) {
        let fillPercentage = (fillAmt / orderAmt) * 100;
        let _value = new BigNumber(fillPercentage.toFixed(2));
        let fillPercentageStr = _value + "%";
        return fillPercentageStr;
      } else {
        return "";
      }
    };

    //arrow function for use in renderer params and %fill.
    let getOrderAmount = (params) => {
      if (
        params.data != null &&
        Array.isArray(params.data.allocationData) &&
        params.data.allocationData[0] instanceof CubiqTS.AllocationDataImpl
      ) {
        return params.data.allocationData[0].allocationQty;
      } else {
        return params.data.orderQty;
      }
    };

    executionAmtCd.cellRendererParams = {
      getOrderAmount: getOrderAmount,
      getExecAmount: (params) => params.data[COLUMN_ORDER_EXECUTED_AMOUNT],
    };

    executionAmtCd.valueGetter = function (params) {
      return params.data[COLUMN_ORDER_EXECUTED_AMOUNT];
    };
    executionAmtCd.getQuickFilterText = function (params) {
      return "";
    };
    if (!this.restoreFromConfig(executionAmtCd)) {
      executionAmtCd.hide = false;
      executionAmtCd.position = this.position.next();
    }

    td.addColumn(executionAmtCd);

    // dealt execution amount
    const executionUsdAmtCd: BfxColDef = this.initColdDef();
    executionUsdAmtCd.headerName = "Fill Amt USD";
    executionUsdAmtCd.field = COLUMN_ORDER_EXECUTED_AMOUNT_USD;
    executionUsdAmtCd.indexType = IndexType.NaturalString;
    executionUsdAmtCd.minWidth = this.oneWs;
    executionUsdAmtCd.width = this.eightWs;
    executionUsdAmtCd.isPriority = true;
    executionUsdAmtCd.cellClass = ["blotter_amt_align"];
    executionUsdAmtCd.valueFormatter = this.formatAmts;
    executionUsdAmtCd.columnFilterType = ColumnFilterType.NUMBER;
    executionUsdAmtCd.columnFilterData = null;
    executionUsdAmtCd.cellRenderer = "fillRenderer";
    executionUsdAmtCd.cellRendererParams = {
      getOrderAmount: (params) => {
        if (
          params.data != null &&
          Array.isArray(params.data.allocationData) &&
          params.data.allocationData[0] instanceof CubiqTS.AllocationDataImpl
        ) {
          return params.data.allocationData[0].allocationQty;
        } else {
          return params.data.orderQty;
        }
      },
      getExecAmount: (params) => params.data[COLUMN_ORDER_EXECUTED_AMOUNT],
    };

    executionUsdAmtCd.valueGetter = function (params) {
      return params.data.usdEquivExecutedQty;
    };
    executionUsdAmtCd.getQuickFilterText = function (params) {
      return "";
    };
    if (!this.restoreFromConfig(executionUsdAmtCd)) {
      executionUsdAmtCd.hide = true;
      executionUsdAmtCd.position = this.position.next();
    }

    td.addColumn(executionUsdAmtCd);

    const remainingAmt: BfxColDef = this.initColdDef();
    remainingAmt.headerName = "Open Amt";
    remainingAmt.field = COLUMN_REMAINING_AMOUNT;
    remainingAmt.indexType = IndexType.NaturalString;
    remainingAmt.minWidth = 90;
    remainingAmt.width = this.eightWs;
    remainingAmt.isPriority = true;
    remainingAmt.cellClass = ["blotter_amt_align"];
    remainingAmt.columnFilterType = ColumnFilterType.NUMBER;
    remainingAmt.getQuickFilterText = () => null;
    remainingAmt.columnFilterData = null;
    remainingAmt.visibleInEmbeddedMode = true;
    remainingAmt.isEmbeddedColumnPriority = true;
    remainingAmt.embeddedModePosition = BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_OPEN_AMT;
    remainingAmt.valueGetter = function (params) {
      return params.data.leavesQty;
    };
    remainingAmt.getQuickFilterText = function (params) {
      return "";
    };

    remainingAmt.valueFormatter = this.formatAmts;
    // if (remainingAmt.visibleInEmbeddedMode && this.restoreFromConfig(remainingAmt)) {
    //     remainingAmt.position = BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_OPEN_AMT;
    // }
    if (!this.restoreFromConfig(remainingAmt)) {
      remainingAmt.position = this.position.next();
    }

    td.addColumn(remainingAmt);

    // dealt usd equiv open(remaining) amount
    const remainingUsdAmtCd: BfxColDef = this.initColdDef();
    remainingUsdAmtCd.headerName = "Open Amt USD";
    remainingUsdAmtCd.field = COLUMN_REMAINING_AMOUNT_USD;
    remainingUsdAmtCd.indexType = IndexType.NaturalString;
    remainingUsdAmtCd.minWidth = this.oneWs;
    remainingUsdAmtCd.width = this.eightWs;
    remainingUsdAmtCd.cellClass = ["blotter_amt_align"];
    remainingUsdAmtCd.valueGetter = function (params) {
      if (
        Array.isArray(params.data.allocationData) &&
        params.data.allocationData.length > 0 &&
        params.data.allocationData[0] instanceof CubiqTS.AllocationDataImpl
      ) {
        return params.data.allocationData[0].usdEquivLeavesQty;
      } else {
        return params.data.usdEquivLeavesQty;
      }
    };
    remainingUsdAmtCd.valueFormatter = this.formatAmts;
    remainingUsdAmtCd.columnFilterType = ColumnFilterType.NUMBER;
    remainingUsdAmtCd.columnFilterData = null;
    remainingUsdAmtCd.getQuickFilterText = function (params) {
      return "";
    };
    if (!this.restoreFromConfig(remainingUsdAmtCd)) {
      remainingUsdAmtCd.hide = true;
      remainingUsdAmtCd.position = this.position.next();
    }

    td.addColumn(remainingUsdAmtCd);

    // product spot/fwd/fwd off/ndf etc
    let prodCol: BfxColDef = this.initColdDef();
    prodCol.headerName = "Product";
    prodCol.field = COLUMN_ORDER_PRODUCT_PRODUCT;
    prodCol.alias = [COLUMN_ORDER_PRODUCT_PRODUCT, COLUMN_INTER_ORDER_PRODUCT];
    prodCol.indexType = IndexType.String;
    prodCol.cellClass = ["blotter_left_align_col"];
    prodCol.minWidth = this.oneWs;
    prodCol.width = this.fourWs;
    prodCol.columnFilterType = ColumnFilterType.ENUM;
    prodCol.columnFilterData = CubiqTS.Product;
    prodCol.valueGetter = function (params) {
      switch (params.data.product) {
        case CubiqTS.Product.SPT:
          return "Spot";
        case CubiqTS.Product.FWD:
          return CubiqTS.RequestTrackingType.OUTRIGHT_RATE === params.data.requestTrackingType
            ? "Fwd"
            : "Fwd (Spot)";
        case CubiqTS.Product.NDF:
          return CubiqTS.RequestTrackingType.OUTRIGHT_RATE === params.data.requestTrackingType
            ? "NDF"
            : "NDF (Spot)";
      }
      return "";
    };
    if (!this.restoreFromConfig(prodCol)) {
      prodCol.hide = true;
      prodCol.position = this.position.next();
    }

    td.addColumn(prodCol);

    // order type
    let orderTypeCd: BfxColDef = this.initColdDef();
    orderTypeCd.headerName = "Order Type";
    orderTypeCd.field = COLUMN_ORDER_TYPE;
    orderTypeCd.cellRenderer = "orderTypeRenderer";
    orderTypeCd.indexType = IndexType.String;
    orderTypeCd.alias = [COLUMN_TRADE_ORDER_TYPE_DATA];
    orderTypeCd.minWidth = this.oneWs;
    orderTypeCd.width = this.sixWs;
    orderTypeCd.cellClass = ["blotter_center_align"];
    orderTypeCd.columnFilterType = ColumnFilterType.ORDERTYPE;
    orderTypeCd.columnFilterData = FilterOrderTypes;
    orderTypeCd.disableInPicker = embeddedMode;
    orderTypeCd.visibleInEmbeddedMode = true;
    orderTypeCd.isEmbeddedColumnPriority = false;
    orderTypeCd.embeddedModePosition =
      BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_ORDER_TYPE;
    orderTypeCd.valueGetter = function (params) {
      return this.myOrderTypeHelper.getType(params.data);
    }.bind(this);
    // if (orderTypeCd.visibleInEmbeddedMode && this.restoreFromConfig(orderTypeCd)) {
    //     orderTypeCd.position = BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_ORDER_TYPE;
    // }
    if (!this.restoreFromConfig(orderTypeCd)) {
      orderTypeCd.hide = !embeddedMode;
      orderTypeCd.position = this.position.next();
    }

    td.addColumn(orderTypeCd);
    ///////////
    // value date / tenor
    // acts on settleTenor as well as displaying label
    let vd: BfxColDef = this.initColdDef();
    vd.headerName = "Value Date";
    vd.field = COLUMN_VALUE_DATE;
    vd.alias = [COLUMN_TRADE_VALUE_DATE_DATA];
    vd.minWidth = this.oneWs;
    vd.width = this.sixWs;
    vd.sortKey = "valueDateSort";
    vd.indexType = IndexType.AgGridDefault;
    vd.columnFilterType =
      this.myFwdEnabled || this.myNdfEnabled ? ColumnFilterType.DATE_TENOR : ColumnFilterType.NONE;
    vd.columnFilterData = this.myFwdEnabled || this.myNdfEnabled ? CubiqTS.Tenor : null;
    vd.valueGetter = function (params) {
      return this.myValueDateHelper.getMoment(params.data);
    }.bind(this);

    vd.valueFormatter = function (params) {
      if (params.value) {
        return this.myValueDateHelper.formatValueDate(params);
      }
      return EMPTY_TEXT_VALUE;
    }.bind(this);

    if (!this.restoreFromConfig(vd)) {
      vd.hide = true;
      vd.position = this.position.next();
    }

    td.addColumn(vd);

    // trader group
    let dkId: BfxColDef = this.initColdDef();
    dkId.headerName = "Trader Group";
    dkId.field = COLUMN_TRADER_GROUP_NAMES; //COLUMN_DESK_ID;
    dkId.alias = [COLUMN_TRADE_DESK_ID, COLUMN_INTER_DESK_ID];
    dkId.minWidth = this.oneWs;
    dkId.width = this.sevenWs;
    dkId.columnFilterType = ColumnFilterType.TRADERGROUP;
    dkId.visibleInEmbeddedMode = true;
    dkId.isEmbeddedColumnPriority = false;
    dkId.disableInPicker = embeddedMode;
    dkId.isPriority = !embeddedMode;
    dkId.embeddedModePosition = BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_TRADER_GROUP;
    dkId.valueGetter = ((params: ValueGetterParams) => {
      // return params.data.blotterMode === BmarkClientOrdersBlotterMode.RISK ?
      //   (this.myBmarkTradeGroupNameHelper as BmarkTraderGroupNameHelper).getTraderGroupNames(params)
      return params.data[COLUMN_TRADER_GROUP_NAMES];
    }).bind(this);

    // ((params: ValueGetterParams) => {
    //     return (this.myBmarkTradeGroupNameHelper as BmarkTraderGroupNameHelper).getTraderGroupNames(params);
    // }).bind(this);
    // if (dkId.visibleInEmbeddedMode && this.restoreFromConfig(dkId)) {
    //     dkId.position = BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_TRADER_GROUP;
    // }
    if (!this.restoreFromConfig(dkId)) {
      dkId.position = this.position.next();
    }

    td.addColumn(dkId);

    let startTimeColumn: BfxColDef = this.initColdDef();

    startTimeColumn.headerName = "Start Time";
    startTimeColumn.field = COLUMN_ORDER_ACTIVATION_DATE;
    startTimeColumn.minWidth = this.oneWs;
    startTimeColumn.width = this.sixWs;
    startTimeColumn.indexType = IndexType.String;
    startTimeColumn.columnFilterType = ColumnFilterType.NONE;
    startTimeColumn.visibleInEmbeddedMode = true;
    startTimeColumn.isEmbeddedColumnPriority = false;
    startTimeColumn.embeddedModePosition =
      BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_START_TIME;
    startTimeColumn.isPriority = !embeddedMode;
    if (!this.restoreFromConfig(startTimeColumn)) {
      startTimeColumn.position = position.next();
    }

    startTimeColumn.valueGetter = BM_ALGO_START_TIME_VALUE_GETTER;

    td.addColumn(startTimeColumn);

    let endTimeColumn: BfxColDef = this.initColdDef();

    endTimeColumn.headerName = "End Time";
    endTimeColumn.field = COLUMN_ORDER_EXPIRY_DATE;
    endTimeColumn.minWidth = this.oneWs;
    endTimeColumn.width = this.sixWs;
    endTimeColumn.indexType = IndexType.Double;
    endTimeColumn.columnFilterType = ColumnFilterType.NONE;
    endTimeColumn.visibleInEmbeddedMode = true;
    endTimeColumn.isEmbeddedColumnPriority = false;
    endTimeColumn.embeddedModePosition =
      BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_END_TIME;
    endTimeColumn.isPriority = !embeddedMode;
    if (!this.restoreFromConfig(endTimeColumn)) {
      endTimeColumn.position = position.next();
    }

    endTimeColumn.valueGetter = BM_ALGO_END_TIME_VALUE_GETTER;

    td.addColumn(endTimeColumn);

    /**
     * Fixing Code column
     */
    let fixingCodeColumn: BfxColDef = this.initColdDef();

    fixingCodeColumn.headerName = "Fixing Code";
    fixingCodeColumn.field = COLUMN_ORDER_FIXING_SOURCE_CODE_DATA;
    fixingCodeColumn.alias = [COLUMN_ORDER_FIXING_SOURCE_CODE_DATA];
    fixingCodeColumn.minWidth = this.oneWs;
    fixingCodeColumn.width = this.sixWs;
    fixingCodeColumn.omitFromPicker = false;
    fixingCodeColumn.columnFilterType = ColumnFilterType.BM_FIXCODE;
    if (!this.restoreFromConfig(fixingCodeColumn)) {
      fixingCodeColumn.position = position.next();
    }

    td.addColumn(fixingCodeColumn);

    /**
     * Fixing Time column
     */
    let fixingTimeColumn: BfxColDef = this.initColdDef();

    fixingTimeColumn.headerName = "Fixing Time";
    fixingTimeColumn.field = "reqFixingTime";
    fixingTimeColumn.columnFilterType = ColumnFilterType.NONE;
    fixingTimeColumn.minWidth = this.oneWs;
    fixingTimeColumn.width = this.fiveWs;
    fixingTimeColumn.cellClass = ["blotter_center_align_col"];
    fixingTimeColumn.valueGetter = FIXING_TIME_VALUE_GETTER.bind(this);
    if (!this.restoreFromConfig(fixingTimeColumn)) {
      fixingTimeColumn.position = position.next();
    }

    td.addColumn(fixingTimeColumn);

    // /**
    //  * Fixing Date column
    //  */
    let fixingDateColumn: BfxColDef = this.initColdDef();

    fixingDateColumn.headerName = "Fixing Date";
    fixingDateColumn.field = "reqFixingDate";
    fixingDateColumn.minWidth = this.oneWs;
    fixingDateColumn.width = this.sixWs;
    fixingDateColumn.indexType = IndexType.Double;
    fixingDateColumn.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(fixingDateColumn)) {
      fixingDateColumn.position = position.next();
    }

    fixingDateColumn.valueGetter = FIXING_DATE_VALUE_GETTER.bind(this);

    td.addColumn(fixingDateColumn);

    /**
     * Fixing Time in UTC terms column
     */
    let fixingTimeUTCColumn: BfxColDef = this.initColdDef();

    fixingTimeUTCColumn.headerName = "Fixing UTC Time";
    fixingTimeUTCColumn.field = "fixingTimeUTC";
    fixingTimeUTCColumn.headerTooltip = "Fixing Time in UTC Terms";
    fixingTimeUTCColumn.minWidth = this.oneWs;
    fixingTimeUTCColumn.width = this.fiveWs;
    fixingTimeUTCColumn.cellClass = ["blotter_center_align_col"];
    fixingTimeUTCColumn.indexType = IndexType.String;
    fixingTimeUTCColumn.columnFilterType = ColumnFilterType.NONE;

    fixingTimeUTCColumn.valueGetter = FIXING_TIME_UTC_VALUE_GETTER.bind(this);
    fixingTimeUTCColumn.valueFormatter = FIXING_TIME_UTC_VALUE_FORMATTER.bind(this);

    if (!this.restoreFromConfig(fixingTimeUTCColumn)) {
      fixingTimeUTCColumn.hide = true;
      fixingTimeUTCColumn.position = position.next();
    }

    td.addColumn(fixingTimeUTCColumn);

    /**
     * Market spot column
     */
    let marketSpotColumn: BfxColDef = this.initColdDef();

    marketSpotColumn.headerName = "Market Spot";
    marketSpotColumn.minWidth = this.oneWs;
    marketSpotColumn.width = this.sevenWs;
    marketSpotColumn.field = "marketFillSptRt";
    marketSpotColumn.cellClass = ["blotter_amt_align"];
    marketSpotColumn.indexType = IndexType.NaturalString;
    marketSpotColumn.columnFilterType = ColumnFilterType.NONE;

    marketSpotColumn.valueGetter = (params: ValueGetterParams) => {
      let order: CubiqTS.OrderDataImpl = params.data as CubiqTS.OrderDataImpl;
      let value: CubiqTS.BigNumber = null;

      if (order.vwapSpotRt != null) {
        value = order.vwapSpotRt;
      } else if (
        // match server behavior OrderDataBuilder:919
        // order.fixingDetails.bmOrderType === CubiqTS.BenchmarkOrderType.PositionOrder &&
        Array.isArray(order.allocationData) &&
        order.allocationData.length === 1
      ) {
        let allocation = order.allocationData[0];
        let fillDetails = allocation?.fillDetails;

        if (BigNumber.isBigNumber(fillDetails?.marketFillSptRt))
          value = fillDetails?.marketFillSptRt;
      }

      return value;
    };

    marketSpotColumn.valueFormatter = this.formatRate.bind(this);
    if (!this.restoreFromConfig(marketSpotColumn)) {
      marketSpotColumn.position = position.next();
    }

    td.addColumn(marketSpotColumn);

    /**
     * Client Spot column
     */
    let clientSpotColumn: BfxColDef = this.initColdDef();

    clientSpotColumn.headerName = "Client Spot";
    clientSpotColumn.field = "clientFillSptRt";
    clientSpotColumn.minWidth = this.oneWs;
    clientSpotColumn.width = this.sevenWs;
    clientSpotColumn.cellClass = ["blotter_amt_align"];
    clientSpotColumn.indexType = IndexType.NaturalString;
    clientSpotColumn.columnFilterType = ColumnFilterType.NONE;

    clientSpotColumn.valueGetter = (params: ValueGetterParams) => {
      let order: CubiqTS.OrderDataImpl = params.data as CubiqTS.OrderDataImpl;
      let value: CubiqTS.BigNumber = null;

      if (order.vwapSpotRt != null) {
        value = order.vwapSpotRt;
      } else if (
        // match server behavior OrderDataBuilder:919
        // order.fixingDetails.bmOrderType === CubiqTS.BenchmarkOrderType.PositionOrder &&
        Array.isArray(order.allocationData) &&
        order.allocationData.length === 1
      ) {
        let allocation = order.allocationData[0];
        let fillDetails = allocation?.fillDetails;

        if (BigNumber.isBigNumber(fillDetails?.clientFillSptRt))
          value = fillDetails?.clientFillSptRt;
      }

      return value;
    };

    clientSpotColumn.valueFormatter = this.formatRate.bind(this);
    if (!this.restoreFromConfig(clientSpotColumn)) {
      clientSpotColumn.hide = true;
      clientSpotColumn.position = position.next();
    }

    td.addColumn(clientSpotColumn);

    /**
     * Markup column
     */
    // Sales Margin
    let salesMarginColumn: BfxColDef = this.initColdDef();

    salesMarginColumn.headerName = "Markup";
    salesMarginColumn.field = "dealtMarkup";
    salesMarginColumn.minWidth = this.oneWs;
    salesMarginColumn.width = this.sixWs;
    salesMarginColumn.cellClass = ["blotter_amt_align"];
    salesMarginColumn.indexType = IndexType.String;
    salesMarginColumn.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(salesMarginColumn)) {
      salesMarginColumn.hide = true;
      salesMarginColumn.position = position.next();
    }

    salesMarginColumn.valueGetter = (params: ValueGetterParams) => {
      let order: CubiqTS.OrderDataImpl = params.data;

      if (order.orderState === CubiqTS.OrderState.FILLED) {
        return this._myDealtPricingService.getDealtMarkup(order) as string;
      } else {
        return this._myDealtPricingService.getEntryMarkup(order) as string;
      }
    };

    td.addColumn(salesMarginColumn);

    // Strategy Params
    let strParameters: BfxColDef = this.initColdDef();
    strParameters.headerName = "Strategy Params";
    strParameters.field = COLUMN_ORDER_STRATEGY_PARAMETERS;
    strParameters.sortingOrder = [];
    strParameters.minWidth = this.oneWs;
    strParameters.width = this.nineWs;
    strParameters.columnFilterType = ColumnFilterType.NONE;
    strParameters.visibleInEmbeddedMode = true;
    strParameters.isEmbeddedColumnPriority = false;
    strParameters.embeddedModePosition =
      BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_ALGO_PARAMS;
    strParameters.valueGetter = function (params) {
      let order: CubiqTS.OrderDataImpl = params.data;
      let strategyParameters = this.atdlTooltipUiRep.inlineUiRep(
        order.algoStrategyName,
        order.strategyParameters,
      );
      let value = "";

      if (strategyParameters != null && Array.isArray(strategyParameters)) {
        for (const [i, strategyParameter] of strategyParameters.entries()) {
          value += `${strategyParameter.parameterName}: ${strategyParameter.parameterValue}`;

          if (i < strategyParameters.length - 1) value += `; `;
        }
      }

      return value;
    }.bind(this);
    strParameters.tooltipValueGetter = MultiLineTooltipValueGetter;
    if (!this.restoreFromConfig(strParameters)) {
      strParameters.hide = embeddedMode;
      strParameters.position = this.position.next();
    }

    td.addColumn(strParameters);

    // entry user name
    let enUser: BfxColDef = this.initColdDef();
    enUser.headerName = "Entry User";
    enUser.field = COLUMN_ENTRY_USER_ID;
    enUser.minWidth = this.oneWs;
    enUser.width = this.sixWs;
    enUser.columnFilterType = ColumnFilterType.USER;
    enUser.visibleInEmbeddedMode = true;
    enUser.isEmbeddedColumnPriority = false;
    enUser.embeddedModePosition = BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_ENTRY_USER;
    enUser.valueGetter = function (params) {
      let user = this._userLookupService.getItem(params.data[COLUMN_ENTRY_USER_ID]);
      if (user) {
        return user.userCode;
      }
      return EMPTY_TEXT_VALUE;
    }.bind(this);
    enUser.valueFormatter = function (params) {
      let user = this._userLookupService.getItem(params.data[COLUMN_ENTRY_USER_ID]);
      if (user) {
        return this.showUserName ? user.userName : user.userCode;
      }
      return EMPTY_TEXT_VALUE;
    }.bind(this);
    // if (enUser.visibleInEmbeddedMode && this.restoreFromConfig(enUser)) {
    //     enUser.position = BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_ENTRY_USER;
    // }
    if (!this.restoreFromConfig(enUser)) {
      enUser.position = this.position.next();
    }

    td.addColumn(enUser);

    // entry date
    let entryDate: BfxColDef = this.initColdDef();
    entryDate.headerName = "Entry Date";
    entryDate.field = COLUMN_ORDER_ENTRY_DATE;
    entryDate.minWidth = this.oneWs;
    entryDate.width = this.sixWs;
    entryDate.columnFilterType = ColumnFilterType.NONE;
    entryDate.visibleInEmbeddedMode = true;
    entryDate.isEmbeddedColumnPriority = false;
    entryDate.embeddedModePosition = BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_ENTRY_DATE;
    entryDate.valueFormatter = this.formatMomment.bind(this);
    // if (entryDate.visibleInEmbeddedMode && this.restoreFromConfig(entryDate)) {
    //     entryDate.position = BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_ENTRY_DATE;
    // }
    if (!this.restoreFromConfig(entryDate)) {
      entryDate.hide = !embeddedMode;
      entryDate.position = this.position.next();
    }

    td.addColumn(entryDate);

    // channel
    let channel: BfxColDef = this.initColdDef();
    channel.headerName = "Channel";
    channel.headerTooltip = "Entry Channel";
    channel.field = "channelCd";
    channel.minWidth = this.oneWs;
    channel.width = this.fiveWs;
    channel.columnFilterType = ColumnFilterType.CHANNEL;
    if (!this.restoreFromConfig(channel)) {
      channel.hide = true;
      channel.position = this.position.next();
    }
    channel.valueGetter = (params) => {
      let channelCd: string;
      channelCd = params.data.channelCd;
      return channelCd != null && typeof channelCd === "string" && channelCd.length > 0
        ? channelCd
        : null;
    };
    channel.valueFormatter = (params) => {
      let channelCd: string = params.data.channelCd;
      let displayName: string;
      if (channelCd != null && typeof channelCd === "string" && channelCd.length > 0) {
        displayName = this._clientLookup.getTradingChannelProfileDisplayName(channelCd);
      }

      return displayName != null && typeof displayName === "string" && displayName.length > 0
        ? displayName
        : EMPTY_TEXT_VALUE;
    };

    td.addColumn(channel);

    // clOrderId
    let clientOrderId: BfxColDef = this.initColdDef();
    clientOrderId.headerName = "ClOrderId";
    clientOrderId.field = "clOrdID";
    clientOrderId.minWidth = this.oneWs;
    clientOrderId.width = this.sixWs;
    clientOrderId.columnFilterType = ColumnFilterType.NONE;
    clientOrderId.visibleInEmbeddedMode = true;
    clientOrderId.isEmbeddedColumnPriority = false;
    clientOrderId.embeddedModePosition =
      BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_EXTERNAL_REF;
    if (!this.restoreFromConfig(clientOrderId)) {
      clientOrderId.hide = true;
      clientOrderId.position = this.position.next();
    }

    td.addColumn(clientOrderId);

    // client branch
    let clientBranch: BfxColDef = this.initColdDef();
    clientBranch.headerName = "Branch";
    clientBranch.headerTooltip = "Client Branch";
    clientBranch.field = COLUMN_CLIENT_BRANCH;
    clientBranch.minWidth = this.oneWs;
    clientBranch.width = this.fourWs;
    clientBranch.columnFilterType = ColumnFilterType.BRANCH;
    if (!this.restoreFromConfig(clientBranch)) {
      clientBranch.hide = true;
      clientBranch.position = this.position.next();
    }

    td.addColumn(clientBranch);

    // salesGroup
    let salesGroup: BfxColDef = this.initColdDef();
    salesGroup.headerName = "Sales Group";
    salesGroup.field = COLUMN_TRADE_SALES_GROUP_ID_DATA;
    salesGroup.minWidth = this.oneWs;
    salesGroup.width = this.sevenWs;
    salesGroup.columnFilterType = ColumnFilterType.SALESGROUP;
    salesGroup.valueGetter = function (params) {
      let sg = this._mySalesGroupLookup.getSalesGroup(
        params.data[COLUMN_TRADE_SALES_GROUP_ID_DATA],
      );
      if (sg) {
        return sg.groupCode;
      }
      return EMPTY_TEXT_VALUE;
    }.bind(this);
    if (!this.restoreFromConfig(salesGroup)) {
      salesGroup.hide = true;
      salesGroup.position = this.position.next();
    }

    td.addColumn(salesGroup);

    // client comments
    let clComm1: BfxColDef = this.initColdDef();

    (clComm1.headerName = "Client Comments"), (clComm1.field = "comments1");

    clComm1.minWidth = this.oneWs;
    clComm1.width = this.nineWs;
    clComm1.showTooltip = true;
    clComm1.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(clComm1)) {
      clComm1.hide = true;
      clComm1.position = this.position.next();
    }

    td.addColumn(clComm1);

    if (this.myPrivateCommentsEnabled) {
      // private comments
      let clComm2: BfxColDef = this.initColdDef();
      clComm2.headerName = "Private Comments";
      clComm2.field = "comments2";
      clComm2.minWidth = this.oneWs;
      clComm2.width = this.nineWs;
      clComm2.showTooltip = true;
      clComm2.columnFilterType = ColumnFilterType.NONE;
      clComm2.visibleInEmbeddedMode = true;
      clComm2.isEmbeddedColumnPriority = false;
      clComm2.embeddedModePosition =
        BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_PRIVATE_COMMENTS;
      // clComm2.position = this.position.next(
      // if (clComm2.visibleInEmbeddedMode && this.restoreFromConfig(clComm2)) {
      //     clComm2.position = BfxHedgeOrderMonitorBlotterColumns.COLUMN_POSITION_PRIVATE_COMMENTS;
      // }
      if (!this.restoreFromConfig(clComm2)) {
        clComm2.hide = !embeddedMode;
        clComm2.position = this.position.next();
      }

      td.addColumn(clComm2);
    }

    // STP
    let stph: BfxColDef = this.initColdDef();
    stph.headerName = "Hold STP";
    stph.field = "stpHold";
    stph.minWidth = this.oneWs;
    stph.width = this.sixWs;
    stph.omitFromPicker = false;
    stph.valueFormatter = this.formatBoolean;
    stph.columnFilterType = ColumnFilterType.BOOLEAN;
    stph.cellClass = [];
    if (!this.restoreFromConfig(stph)) {
      stph.hide = true;
      stph.position = this.position.next();
    }
    td.addColumn(stph);

    /**
     * Allocation id column
     */
    let allocIdColumn = this.initColdDef();

    allocIdColumn.headerName = "Allocation ID";
    allocIdColumn.field = COLUMN_ALLOCATION_ID;
    allocIdColumn.minWidth = this.oneWs;
    allocIdColumn.width = BfxConfigIDRefHelper.getAllocationRefWs();
    allocIdColumn.indexType = IndexType.Integer;
    allocIdColumn.ColumnFilterType = ColumnFilterType.NONE;

    allocIdColumn.valueGetter = (params: ValueGetterParams) => {
      let order: CubiqTS.OrderDataImpl = params.data as CubiqTS.OrderDataImpl;
      if (order.orderType === CubiqTS.OrderType.BMALGO) {
        // BMALGO's don't display Allocation IDs
        return EMPTY_TEXT_VALUE;
      }
      if (order.allocationData != null) {
        return order.allocationData[0][COLUMN_ALLOCATION_ID].toString();
      }
      return EMPTY_TEXT_VALUE;
    };
    allocIdColumn.valueFormatter = BfxConfigIDRefHelper.orderAllocationRefValueFormatter;
    allocIdColumn.getQuickFilterText = BfxConfigIDRefHelper.orderAllocationRefValueFormatter;
    if (!this.restoreFromConfig(allocIdColumn)) {
      allocIdColumn.hide = true;
      allocIdColumn.position = position.next();
    }

    td.addColumn(allocIdColumn);

    /**
     * Fill date
     */
    let fillDateColumn = this.initColdDef();

    fillDateColumn.headerName = "Fill Date";
    fillDateColumn.field = "fillDts";
    fillDateColumn.minWidth = this.oneWs;
    fillDateColumn.width = this.eightWs;
    fillDateColumn.indexType = IndexType.Double;
    fillDateColumn.omitFromPicker = false;
    fillDateColumn.columnFilterType = ColumnFilterType.NONE;
    fillDateColumn.valueFormatter = this.formatDate.bind(this, "DD-MMM-YY");
    fillDateColumn.valueGetter = function (params) {
      let order: CubiqTS.OrderDataImpl = params.data;
      if (
        order != null &&
        (order.fillState == CubiqTS.FillState.FILLED ||
          order.fillState == CubiqTS.FillState.PARTFILLED) &&
        order.lastFillDetails != null
      ) {
        let lastFill: CubiqTS.LastFillDetailsImpl =
          order.lastFillDetails as CubiqTS.LastFillDetailsImpl;
        return lastFill.fillDts;
      }
      if (
        order != null &&
        (order.fillState == CubiqTS.FillState.FILLED ||
          order.fillState == CubiqTS.FillState.PARTFILLED) &&
        order.allocationData != null &&
        order.allocationData.length > 0 &&
        order.allocationData[0].fillDetails != null
      ) {
        let lastFill: CubiqTS.LastFillDetailsImpl = order.allocationData[0]
          .fillDetails as CubiqTS.LastFillDetailsImpl;
        return lastFill.fillDts;
      }
    };
    if (!this.restoreFromConfig(fillDateColumn)) {
      fillDateColumn.hide = false;
      fillDateColumn.position = this.position.next();
    }

    td.addColumn(fillDateColumn);

    // dealt Volume per minute amount
    const volumePerMinuteColumn: BfxColDef = this.initColdDef();
    volumePerMinuteColumn.headerName = "Vol/Min";
    volumePerMinuteColumn.field = "volumePerMinuteQty";
    volumePerMinuteColumn.indexType = IndexType.NaturalString;
    volumePerMinuteColumn.minWidth = this.oneWs;
    volumePerMinuteColumn.width = this.eightWs;
    volumePerMinuteColumn.isPriority = true;
    volumePerMinuteColumn.cellClass = ["blotter_amt_align"];
    volumePerMinuteColumn.columnFilterType = ColumnFilterType.NUMBER;
    volumePerMinuteColumn.columnFilterData = null;
    volumePerMinuteColumn.visibleInEmbeddedMode = true;
    volumePerMinuteColumn.valueGetter = function (params) {
      let order: CubiqTS.OrderDataImpl = params.data;

      if (order != null && !this.volumePerMinuteHelper.isSupportedOrder(order)) {
        return null;
      }

      let amount: CubiqTS.BigNumber = params.data[COLUMN_REMAINING_AMOUNT];

      if (amount != null) {
        amount = this.volumePerMinuteHelper.getVolumePerMinuteAmount(amount, order);
      }

      return amount;
    }.bind(this);
    volumePerMinuteColumn.valueFormatter = this.formatAmtsExp;
    if (!this.restoreFromConfig(volumePerMinuteColumn)) {
      volumePerMinuteColumn.position = 100;
    }

    td.addColumn(volumePerMinuteColumn);

    if (embeddedMode) {
      let embeddedColumns: Array<BfxColDef> = td.columns.filter(
        (column: BfxColDef) => column.visibleInEmbeddedMode,
      );
      td.columns = [];
      td.columnIndexMap = new Map();
      embeddedColumns.forEach(td.addColumn.bind(td));
    }

    this.processQuickFilterColumns(td);

    return td;
  }

  protected processQuickFilterColumns(tableDefinition: TableDefinition): void {
    tableDefinition.columns.forEach((column: BfxColDef) => {
      if (column.getQuickFilterText == null) {
        column.getQuickFilterText = this.quickFilterText.bind(this);
      }
    });
  }

  public quickFilterText(params: GetQuickFilterTextParams): string {
    let value: string = null;

    if (
      params.column.isVisible() &&
      params.value != null &&
      typeof params.value !== "string" &&
      typeof params.colDef.valueFormatter === "function"
    ) {
      if (params.value instanceof CubiqTS.BigNumber) {
        value = params.value.toString();
      } else {
        value = params.colDef.valueFormatter(params as ValueFormatterParams);
      }
    } else if (params.column.isVisible() && params.value != null) {
      value = params.value + "";
    }

    return value;
  }

  public getBlotterType() {
    return BlotterType.ORDER;
  }

  public getAssetClass() {
    return AssetClass.FIX;
  }

  /**
   * Helper functions
   */

  private mapUserSettings(profileValue: any) {
    if (profileValue) {
      let savedSettings = JSON.parse(profileValue);
      if (savedSettings != null) {
        super.determineDefaultConfig(savedSettings);
        // if (savedSettings.hasOwnProperty('buyAndSellModel')
        // && savedSettings.buyAndSellModel !== null && savedSettings.buyAndSellModel == 'y') {
        //     this.isIntentStyled = true;
        // } else {
        //     this.isIntentStyled = false;
        // }
      }
    } else {
      logger.out.warn(
        "BmarkClientOrdersTableDefinitionFactory --> mapUserSettings no profile value " +
          profileValue,
      );
    }
  }

  public getBlotterVersion(): string {
    return HEDGE_ORDERS_TABLE_DEFINITION_VER;
  }

  protected getProfileSettingKey(): string {
    return `${ProfileSettingKeys.userProfileDefaultBlotter}bmarkHedgeOrders`;
  }

  public formatRate(params: ValueFormatterParams) {
    if (params.value == null) {
      return EMPTY_AMT_RATE_VALUE;
    }

    let value: any = params.value;
    if (typeof params.value == "string") {
      value = new CubiqTS.BigNumber(params.value);
    }

    let minPrecision: number = 0;
    let order: CubiqTS.OrderDataImpl = params.data;
    let ccyPair: CubiqTS.CurrencyPairImpl = this._ccyPairLookupService.getCcyPair(
      order.baseCode,
      order.nonBaseCode,
    );

    if (ccyPair) {
      if (order.isInvertedOrderFg) {
        minPrecision = ccyPair.spotInvertDecimalsQty;
      } else {
        minPrecision = ccyPair.pipsQty;
      }
    }

    let currentDecs: number = value.decimalPlaces();
    if (currentDecs < minPrecision) {
      return value.toFixed(minPrecision);
    }

    return value.toFixed(currentDecs);
  }

  protected formatAmtsExp(params: ValueFormatterParams): string {
    if (params.value) {
      if (params.value.gte(1e10)) {
        return params.value.toExponential(3);
      }
      return params.value.toFormat(0);
    }
    return EMPTY_AMT_RATE_VALUE;
  }
}
